import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { Compare } from "@/components/guides/Compare";
import { TryTool } from "@/components/guides/TryTool";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["1d-vs-2d-barcodes"];

const TOC = [
  { id: "the-short-answer", label: "The short answer" },
  { id: "at-a-glance", label: "1D vs 2D at a glance" },
  { id: "how-they-differ", label: "How they actually differ" },
  { id: "when-1d", label: "When 1D is still the right answer" },
  { id: "when-2d", label: "When 2D earns its place" },
  { id: "sunrise-2027", label: "Sunrise 2027 and dual carriers" },
  { id: "scanner-support", label: "Scanner support" },
  { id: "common-mistakes", label: "Common mistakes" },
  { id: "quick-decision", label: "Quick decision guide" },
  { id: "scanners-compared", label: "1D vs 2D Barcode Scanners" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "What's the Actual Difference Between a 1D and a 2D Barcode?",
    a: "A 1D barcode encodes data along a single horizontal axis using vertical bars of varying width. A 2D barcode encodes data in a square or rectangular matrix of modules, using both axes. The matrix layout gives 2D codes far more capacity in the same physical footprint, plus built-in error correction.",
  },
  {
    q: "Is a QR Code a 2D Barcode?",
    a: "Yes. QR (Quick Response) is one of several 2D matrix symbologies, alongside Data Matrix, Aztec and PDF417. Whenever someone says 'barcode vs QR code', they really mean 1D vs 2D — QR is just the most familiar 2D format because of phone cameras.",
  },
  {
    q: "Are 2D Barcodes Always Better?",
    a: "Not at retail point-of-sale. For a fixed product where the till only needs the GTIN, an EAN-13 or UPC-A is the fastest, most reliable scan. 2D wins when you need to encode batch, expiry, serial number or a consumer URL alongside the GTIN — which is exactly what GS1 Sunrise 2027 is pushing for on consumer packs.",
  },
  {
    q: "Can My Phone Read 1D Barcodes?",
    a: "Modern iOS and Android cameras read most 1D formats (EAN, UPC, Code 128, Code 39) and every common 2D format (QR, Data Matrix, PDF417, Aztec). Older phones and embedded scanners may be limited to a subset — always test with the device class your customers actually use.",
  },
  {
    q: "Why Does GS1 Prefer Data Matrix Over QR for Healthcare?",
    a: "Data Matrix achieves the same data density at smaller print sizes, which matters on vials, surgical instruments and unit-dose packaging. QR is favoured for consumer-facing applications where the symbol is bigger and phones are the primary scanner.",
  },
  {
    q: "Will I Need to Replace My 1D Barcode Equipment?",
    a: "Probably, but gradually. Most older laser scanners are 1D-only. Imager-based scanners (the ones with a red illumination box rather than a single line) handle both. Audit your fleet before Sunrise 2027 and prioritise high-traffic lanes for the upgrade.",
  },
  {
    q: "Are 2D Barcodes and QR Codes the Same?",
    a: "No. A QR Code is one type of 2D barcode; Data Matrix, Aztec and PDF417 are others. Every QR Code is a 2D barcode, but the 2D code on a medicine pack or a boarding pass is often a different symbology that a QR-only app will not read.",
  },
  {
    q: "Is There Such a Thing as a 3D Barcode?",
    a: "Not as a separate standard symbology. The term usually means a 1D or 2D code that is engraved, embossed or molded into a surface and read from the depth of the marks instead of printed ink contrast. The data inside is still an ordinary symbol, such as a Data Matrix dot-peened onto a metal part.",
  },
];

export const Route = createFileRoute("/barcode-guides/1d-vs-2d-barcodes")({
  component: OneDvsTwoDGuide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function OneDvsTwoDGuide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/sunrise-2027-explained",
          label: "Sunrise 2027 Guide",
          desc: "What the 2D transition at retail point-of-sale actually means for brands.",
        },
        {
          to: "/barcode-guides/barcode-formats",
          label: "Barcode formats field guide",
          desc: "Where 1D and 2D symbologies sit alongside each other in practice.",
        },
        {
          to: "/qr-code-generator",
          label: "QR Code generator",
          desc: "Make a 2D test code and download it as PNG or SVG.",
        },
        {
          to: "/data-matrix-generator",
          label: "Data Matrix generator",
          desc: "Encode text, UDI or GS1 data as a compact 2D symbol.",
        },
      ]}
    >
      <p>
        A pharmacy shipment arrives with two barcodes on every carton. The till scanner reads the
        EAN-13 across the bottom and rings up the right product. The second symbol — a small
        black-and-white square next to it — carries the batch and expiry, but the scanner
        doesn&apos;t register it at all. The shipment goes through, the batch data never lands in
        the system, and a year later a recall traces back to a different wholesaler with no way to
        confirm which units were affected.
      </p>
      <p>
        That is the practical difference between <strong>1D and 2D barcodes</strong>. The
        <em> bars</em> on the side of every retail pack are a 1D barcode — they encode a single
        number along one axis. The <em>squares of dots</em> (QR codes, Data Matrix codes, PDF417,
        Aztec) are 2D barcodes — they pack a real payload of data into a matrix of modules. The
        difference between <strong>1D vs QR code</strong> isn&apos;t really about QR; it&apos;s
        about how much information a symbol can hold and what kind of scanner it takes to read it.
        This guide walks through capacity, scanner support, when each one earns its place, and the
        GS1 Sunrise 2027 migration that&apos;s pushing 2D onto consumer packs by the end of the
        decade.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          A 1D barcode encodes a number along a single horizontal axis. A 2D barcode encodes a
          payload — text, a URL, structured GS1 data — across a square or rectangular matrix. The 2D
          form is denser and carries error correction, so it survives partial damage that would
          break a 1D scan. Modern phone cameras read both; older laser scanners typically read only
          1D.
        </p>

        <Compare
          panes={[
            {
              label: "1D · EAN-13",
              caption: "13-digit identifier encoded as parallel bars. Reads at retail POS.",
              children: (
                <BarcodeFigure
                  format="ean13"
                  value="5012345678900"
                  formatLabel="EAN-13 (1D)"
                  caption={
                    <>
                      Example EAN-13 encoding the GTIN-13 <code>5012345678900</code> as parallel
                      vertical bars along one axis.
                    </>
                  }
                />
              ),
            },
            {
              label: "2D · QR Code",
              caption:
                "Same identifier embedded in a GS1 Digital Link URL — phones open it as a web page.",
              children: (
                <BarcodeFigure
                  format="qrcode"
                  value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
                  formatLabel="QR Code (2D)"
                  caption={
                    <>
                      Example QR encoding a GS1 Digital Link URL with the same GTIN plus batch (
                      <code>10/ABC123</code>) and expiry (<code>17/261231</code>).
                    </>
                  }
                />
              ),
            },
          ]}
        />
      </GuideArticleSection>

      <GuideArticleSection id="at-a-glance" title="1D vs 2D at a Glance">
        <p>
          The big differences between 1D and 2D barcodes line up neatly when you put them next to
          each other. Use this as the quick-reference table; the rest of the guide unpacks each row
          in context.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Side-by-side comparison of 1D and 2D barcodes: shape, capacity, error correction,
              scanner type, footprint and typical use case.
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  Aspect
                </th>
                <th scope="col">
                  1D barcodes
                </th>
                <th scope="col">
                  2D barcodes
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">
                  Shape
                </th>
                <td>
                  Parallel vertical bars along one axis
                </td>
                <td>
                  Square or rectangular matrix of modules
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Common formats
                </th>
                <td>
                  UPC-A, EAN-13, EAN-8, Code 128, Code 39, ITF-14
                </td>
                <td>
                  QR Code, Data Matrix, PDF417, Aztec
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Typical capacity
                </th>
                <td>
                  8–13 digits (retail), up to ~80 characters (Code 128)
                </td>
                <td>
                  Hundreds to thousands of characters
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Error correction
                </th>
                <td>Check digit only (typo detection)</td>
                <td>
                  Reed–Solomon up to ~30% module loss
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Scanner type
                </th>
                <td>Laser line or imager</td>
                <td>Imager (cameras count); stacked PDF417 also reads on raster lasers</td>
              </tr>
              <tr>
                <th scope="row">
                  Footprint
                </th>
                <td>Wide, short — needs label width</td>
                <td>
                  Square — works on small or narrow packs
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Where you see it
                </th>
                <td>
                  Retail POS, parcels, library books, basic logistics
                </td>
                <td>
                  Pharma traceability, boarding passes, consumer Digital Link, IDs
                </td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      </GuideArticleSection>

      <GuideArticleSection id="how-they-differ" title="How They Actually Differ">
        <p>
          A 1D barcode is a one-dimensional language: each bar and gap has a width that encodes a
          digit or character. A scanner draws an imaginary horizontal line across the symbol and
          reads the widths in order. That works beautifully for short identifiers — twelve or
          thirteen digits, the lifetime of every UPC-A or EAN-13 — but it runs out of room very
          quickly. Push past about eighty characters in Code 128 and the barcode is too long to
          print on most labels, never mind to scan reliably.
        </p>
        <p>
          A 2D barcode flips the geometry. Instead of one axis, the data is distributed across a
          matrix of light and dark modules, plus a set of finder and timing patterns that tell the
          scanner how the symbol is oriented. That second axis is worth orders of magnitude in
          capacity: a small Data Matrix can hold a hundred characters in the same footprint as a
          five-digit number on a 1D code; a full QR Code can carry thousands of characters and a
          long URL.
        </p>
        <p>
          The other thing the matrix layout buys you is <strong>error correction</strong>. 2D
          symbologies use Reed–Solomon codes to repair up to roughly 30% of the symbol if it&apos;s
          smudged, scratched or partially obscured — the same maths that lets a phone scan a QR code
          even when a hand or a coffee stain covers a corner. 1D barcodes have a single trailing
          check digit, which catches the most common typing errors but doesn&apos;t actually rebuild
          damaged bars.
        </p>
        <Compare
          panes={[
            {
              label: "2D · Data Matrix",
              caption: "Compact 2D — dominant in pharma, healthcare and component marking.",
              children: (
                <BarcodeFigure
                  format="datamatrix"
                  value="(01)05012345678900(10)ABC123(17)261231"
                  formatLabel="Data Matrix (2D)"
                  caption={
                    <>
                      GS1 Data Matrix carrying a GTIN, batch and expiry in a footprint that fits on
                      a vial cap.
                    </>
                  }
                />
              ),
            },
            {
              label: "2D · PDF417",
              caption: "Stacked 2D — used on driver's licences and shipping labels.",
              children: (
                <BarcodeFigure
                  format="code128"
                  value="PDF417_demo_payload"
                  formatLabel="Code 128 (1D, for contrast)"
                  caption={
                    <>
                      Same ~20 characters as Code 128 (1D) for visual contrast — note how much
                      horizontal space a 1D symbol needs even for short data.
                    </>
                  }
                />
              ),
            },
          ]}
        />
      </GuideArticleSection>

      <GuideArticleSection id="when-1d" title="When 1D Is Still the Right Answer">
        <p>
          The 2D-everywhere story sometimes oversells the case. There are workflows where a plain 1D
          barcode is still cheaper, faster and entirely sufficient:
        </p>
        <ul>
          <li>
            <strong>Retail point-of-sale on a fixed product.</strong> The till already knows the
            GTIN maps to a price and a description; an EAN-13 or UPC-A is the fastest scan in that
            loop. Adding a 2D code there doesn&apos;t make the till faster — it just adds artwork.
          </li>
          <li>
            <strong>Legacy supply chains running laser-only fleets.</strong> Many warehouse
            scanners, library check-out machines and self-service kiosks are 1D-only. As long as
            that hardware is in the loop, 1D has to stay on the label.
          </li>
          <li>
            <strong>Narrow labels.</strong> A Code 128 fits well on a long thin label (cable wrap,
            archival spine, hospital wristband). A 2D code on the same strip is either too small to
            scan reliably or wastes the width.
          </li>
          <li>
            <strong>Wedge-scanner workflows.</strong> Office scanners that emulate keyboard input
            (the &quot;USB wedge&quot;) often work best with simple 1D symbologies piped straight
            into a text field.
          </li>
        </ul>
        <p>
          If you&apos;re generating new 1D codes — for cartons, parcels or internal inventory — the <Link to="/barcode-generator">Barcode Generator</Link> covers UPC, EAN, Code 128, Code 39
          and ITF-14. For carton-level shipping labels with bearer bars, see the <Link to="/itf-14-label-generator">ITF-14 Carton Label Generator</Link>.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="when-2d" title="When 2D Earns Its Place">
        <p>
          A 2D code earns its place whenever a single identifier isn&apos;t enough — when you need
          to carry the GTIN <em>plus</em> the batch, the expiry, the serial, or a URL the shopper
          can scan with their phone. Four scenarios cover most of it:
        </p>
        <ul>
          <li>
            <strong>Track-and-trace regulations.</strong> Pharma, tobacco, medical devices and
            increasingly food categories require batch and expiry encoded on every saleable unit. A
            1D EAN can&apos;t fit that; a GS1 Data Matrix can.
          </li>
          <li>
            <strong>Consumer engagement with no app.</strong> A QR code carrying a GS1 Digital Link
            URL behaves as a normal URL when scanned by a phone — opens the product page, recipe,
            allergen list or warranty form. Same code, scanned at the till by an upgraded POS, also
            returns the GTIN.
          </li>
          <li>
            <strong>Tight packaging where a 1D wouldn&apos;t fit.</strong> A square 2D symbol scales
            down further than the equivalent 1D before quality drops below the printable grade. That
            matters on cosmetics, blister packs and small component reels.
          </li>
          <li>
            <strong>Documents and IDs.</strong> Driver&apos;s licences, boarding passes and shipping
            manifests all use 2D codes (PDF417, Aztec, Data Matrix) to carry structured data
            that&apos;s read by a single camera scan.
          </li>
        </ul>
        <p>
          If you receive a 2D payload and want to see what&apos;s inside, the <Link to="/gs1-digital-link-decoder">GS1 Digital Link Decoder</Link> parses both the URL
          form and the raw bracketed AI form (<code>(01)…(10)…(17)…</code>) and shows each
          Application Identifier expanded into its meaning. Try it with the same payload that&apos;s
          encoded into the QR code above:
        </p>
        <TryTool
          to="/gs1-digital-link-decoder"
          label="GS1 Digital Link Decoder"
          description="Parse a GS1 Digital Link URL or raw AI string into structured GTIN + batch + expiry data."
          value="https://id.gs1.org/01/05012345678900/10/ABC123?17=261231"
          cta="Decode the worked example"
        />
        <p>
          To generate your own 2D codes for proofing, use the <Link to="/qr-code-generator">QR Code Generator</Link> for consumer-facing payloads, the <Link to="/data-matrix-generator">Data Matrix Generator</Link> for compact industrial use,
          or the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link> for a
          Sunrise-ready dual-purpose code.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="sunrise-2027" title="Sunrise 2027 and Dual Carriers">
        <p>
          GS1 Sunrise 2027 is the global commitment that retail point-of-sale scanners will accept
          2D codes (a QR carrying a GS1 Digital Link, or a GS1 Data Matrix) alongside the
          traditional 1D UPC and EAN by the end of 2027. It is the formal acceptance that one symbol
          on the front of a consumer pack should carry both the GTIN that the till needs and the
          structured data (batch, expiry, serial, URL) that the shopper and the supply chain need.
        </p>
        <Callout variant="in-practice" title="The dual-carrier interim">
          <p>
            Until Sunrise 2027 actually lands and most POS fleets are upgraded, almost everyone runs <em>both</em> symbols on the same artwork: a 1D EAN-13 across the base of the pack for
            the till, plus a 2D Digital Link QR or GS1 Data Matrix alongside it for the phone, the
            regulator and the warehouse. Stripping the 1D barcode before your retail partners are
            ready is the single biggest mistake to avoid.
          </p>
        </Callout>
        <p>
          For the full timeline, the equipment audit checklist and the practical migration path, see
          the <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 Guide</Link>.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="scanner-support" title="Scanner Support">
        <p>
          Whether a given scanner reads 1D, 2D or both comes down to its imaging hardware rather
          than its age:
        </p>
        <ul>
          <li>
            <strong>Laser scanners</strong> — the original supermarket scanner shape, a single thin
            red line — read 1D codes only. They&apos;re cheap, fast on a fixed product, and still
            dominant in legacy POS lanes.
          </li>
          <li>
            <strong>Imager scanners</strong> — a red illumination rectangle, often with a visible
            aiming dot — capture a 2D image of the symbol and decode it in firmware. They read every
            1D format too, so an imager is a strict superset of a laser.
          </li>
          <li>
            <strong>Phone cameras</strong> on modern iOS and Android handsets read both 1D and 2D
            natively in the camera app — no third-party app required for the common formats.
          </li>
        </ul>
        <p>
          To test what a given device reads in practice, scan a sample with the <Link to="/barcode-reader">Online Barcode Reader</Link> (auto-detects 1D and 2D), or use
          the format-specific <Link to="/qr-code-reader">QR Code Reader</Link> and <Link to="/data-matrix-reader">Data Matrix Reader</Link> when you know the symbology.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="common-mistakes" title="Common Mistakes">
        <p>
          The same misjudgements come up over and over when teams first add 2D to a 1D workflow:
        </p>
        <ul>
          <li>
            <strong>Treating QR as a generic 2D format for retail.</strong> A plain marketing URL
            inside a QR doesn&apos;t carry the GS1 identifiers that a Sunrise-ready POS expects.
            Encode a <Link to="/gs1-digital-link-generator">GS1 Digital Link</Link> URL instead so
            the same code works at the till and on the phone.
          </li>
          <li>
            <strong>Shrinking a 2D code below its minimum module size.</strong> 2D codes need a
            print grade above a minimum to scan reliably. Compressing one to fit a small label kills
            the error-correction budget and the symbol fails on cheap scanners first.
          </li>
          <li>
            <strong>Removing the 1D barcode the moment the 2D one is added.</strong> Retail POS
            isn&apos;t fully 2D-ready yet. Run dual carriers until your top retailer partners
            certify their scanners — usually well after Sunrise 2027.
          </li>
          <li>
            <strong>Forgetting the FNC1 prefix on a GS1 Data Matrix.</strong> A GS1 Data Matrix
            needs the FNC1 leading modifier so downstream parsers recognise it as GS1 — without it,
            the same string is treated as a plain Data Matrix and the Application Identifiers
            don&apos;t parse.
          </li>
          <li>
            <strong>Assuming every phone scans every format.</strong> Older Android camera apps
            don&apos;t handle Data Matrix or PDF417 natively. Test on the device classes your
            shoppers actually carry, not the latest flagship.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="quick-decision" title="Quick Decision Guide">
        <p>
          If you only need the practical answer for a new label or pack, the rules collapse to four
          short ones:
        </p>
        <ul>
          <li>
            <strong>Use a 1D barcode</strong> when the receiving scanner is laser-based, when the
            data is a fixed identifier (GTIN, ISBN, internal SKU), and when label real estate runs
            horizontally.
          </li>
          <li>
            <strong>Use a 2D barcode</strong> when you need to carry a payload beyond a single
            number — batch, expiry, serial, a URL — or when the packaging is too small for a usable
            1D symbol.
          </li>
          <li>
            <strong>Run both carriers</strong> on consumer packs through the Sunrise 2027
            transition: a 1D EAN-13 for the till, a 2D QR with a GS1 Digital Link for the phone and
            the supply chain.
          </li>
          <li>
            <strong>Validate before printing.</strong> Decode every 2D payload with the <Link to="/gs1-digital-link-decoder">GS1 Digital Link Decoder</Link> and check each 1D
            check digit with the <Link to="/gtin-validator">GTIN Validator</Link>.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="scanners-compared" title="1D vs 2D Barcode Scanners">
        <p>
          A 1D scanner reads only linear barcodes, while a 2D scanner captures an image and reads
          both linear and matrix codes. The difference between 1D and 2D barcode scanners lies in the
          sensor, not in the shape of the housing.
        </p>
        <p>
          A 1D scanner either sweeps a laser line or uses a linear imager, a single row of sensor
          pixels that samples one slice of the symbol. Both measure bar and space widths along that
          one line, so the line has to cross every bar. One slice of a QR Code or Data Matrix holds
          only a fragment of the data, which is why a 1D scanner cannot decode a matrix symbol.
        </p>
        <p>
          A 2D scanner, also called an area imager, takes a full picture and locates the symbol in
          software. It reads a code at any rotation, copes with codes shown on a phone screen, and
          can use the error correction built into a 2D symbol to read past a scratch or smudge.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Comparison of 1D scanners (laser or linear imager) and 2D area imagers: formats read,
              orientation, screens and damaged codes.
            </caption>
            <thead>
              <tr>
                <th scope="col">Aspect</th>
                <th scope="col">1D scanner (laser or linear imager)</th>
                <th scope="col">2D scanner (area imager)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">UPC, EAN, Code 128, ITF-14</th>
                <td>Yes</td>
                <td>Yes</td>
              </tr>
              <tr>
                <th scope="row">QR Code, Data Matrix, Aztec</th>
                <td>No</td>
                <td>Yes</td>
              </tr>
              <tr>
                <th scope="row">Orientation</th>
                <td>Scan line must cross every bar</td>
                <td>Any rotation</td>
              </tr>
              <tr>
                <th scope="row">Codes on a phone screen</th>
                <td>Often unreliable with lasers</td>
                <td>Yes</td>
              </tr>
              <tr>
                <th scope="row">Damaged symbols</th>
                <td>Needs one clean line through the bars</td>
                <td>Matrix codes rebuild lost modules with error correction</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
        <h3>Can a 2D Scanner Read 1D Barcodes?</h3>
        <p>
          Yes. A 2D area imager reads 1D barcodes as well as 2D ones, so a single 2D scanner handles
          both the EAN-13 and the QR Code on the same pack. The reverse does not hold: a laser or
          linear 1D scanner cannot decode a matrix symbol. For hardware, that settles most of the
          1D barcode vs 2D barcode question: an area imager keeps every existing label readable.
        </p>
        <p>
          When a 2D scanner seems to ignore a 1D barcode, the cause is usually its configuration.
          Many scanners ship with some symbologies switched off, such as ITF or Code 39, and need
          them enabled with the programming barcodes in the manual. A missing quiet zone or a crease
          across the bars can also stop the read, since a 1D code has no error correction to fall
          back on.
        </p>
        <p>
          Before buying hardware, test it with 1D and 2D barcode examples taken from your own
          labels: an EAN-13, a Code 128 shipping label and a GS1 DataMatrix, say. To make 2D test
          codes at the size you plan to print, the <Link to="/data-matrix-generator">Data Matrix Generator</Link> and the <Link to="/qr-code-generator">QR Code Generator</Link> both download PNG or SVG files.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "1D barcodes encode a single identifier along one axis; 2D barcodes encode a payload across a matrix and add error correction.",
          "Modern phones and imager scanners read both; older laser scanners read 1D only.",
          "Retail POS still favours 1D for the speed; 2D wins anywhere you need batch / expiry / serial or a consumer URL.",
          "GS1 Sunrise 2027 commits global POS to accept 2D alongside 1D — but dual carriers stay the safe artwork through and past the deadline.",
          "Validate every payload before print: GTIN Validator for 1D check digits, GS1 Digital Link Decoder for 2D structured data.",
        ]}
      />


      <GuideArticleSection id="references" title="References">
        <p>The format and scanner details in this guide follow the GS1 documents below.</p>
        <ul>
          <li>
            GS1 General Specifications — the canonical document defining EAN/UPC, GS1 Data Matrix
            and the Application Identifier syntax. Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a>
            .
          </li>
          <li>
            GS1 Sunrise 2027 programme — overview of the global 2D-at-POS commitment and its
            milestones: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
          <li>
            GS1 Digital Link — the URL form that lets a single 2D symbol serve both the
            point-of-sale scanner and the shopper&apos;s phone: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        For a side-by-side reference of every 1D and 2D symbology you&apos;re likely to meet, see
        the <Link to="/barcode-guides/barcode-formats">barcode formats field guide</Link>. For the
        full Sunrise 2027 timeline and equipment audit, see the dedicated <Link to="/barcode-guides/sunrise-2027-explained">Sunrise 2027 Guide</Link>.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
